/*
  SMA — Product carousel

  Progressively enhances a Dawn `.grid.product-grid` into a swipeable
  carousel. Scrolling itself is native (CSS scroll-snap), so swipe works on
  touch and trackpads with no JavaScript at all — this file only adds the
  arrows, the dots, and the logic that hides both when every card already
  fits on screen.

  Markup contract (see sections/related-products.liquid):

    <div class="sma-carousel" data-sma-carousel>
      ...
      <button data-sma-carousel-prev>…</button>
      <button data-sma-carousel-next>…</button>
      <ul class="grid product-grid sma-carousel__track" data-sma-carousel-track>…</ul>
      <div data-sma-carousel-dots></div>
    </div>

  Containers whose contents arrive later (product recommendations and
  recently viewed are both fetched after page load) are picked up via a
  MutationObserver on any element carrying `data-sma-carousel-host`.
*/
(function () {
  var CAROUSEL = '[data-sma-carousel]';
  var HOST = '[data-sma-carousel-host]';

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function Carousel(root) {
    this.root = root;
    this.track = root.querySelector('[data-sma-carousel-track]');
    this.prevButton = root.querySelector('[data-sma-carousel-prev]');
    this.nextButton = root.querySelector('[data-sma-carousel-next]');
    this.dotsContainer = root.querySelector('[data-sma-carousel-dots]');
    this.pages = 0;
    this.currentPage = 0;
    this.ticking = false;

    if (!this.track) return;

    this.onScroll = this.onScroll.bind(this);
    this.refresh = this.refresh.bind(this);

    this.track.addEventListener('scroll', this.onScroll, { passive: true });

    if (this.prevButton) {
      this.prevButton.addEventListener('click', this.scrollByPage.bind(this, -1));
    }
    if (this.nextButton) {
      this.nextButton.addEventListener('click', this.scrollByPage.bind(this, 1));
    }

    /* Keyboard support once the track itself has focus. */
    this.track.addEventListener(
      'keydown',
      function (event) {
        if (event.key === 'ArrowRight') {
          this.scrollByPage(1);
        } else if (event.key === 'ArrowLeft') {
          this.scrollByPage(-1);
        } else {
          return;
        }
        event.preventDefault();
      }.bind(this)
    );

    if (typeof ResizeObserver === 'function') {
      this.resizeObserver = new ResizeObserver(this.refresh);
      this.resizeObserver.observe(this.track);
    }
    window.addEventListener('resize', this.refresh);

    this.refresh();
  }

  Carousel.prototype.itemCount = function () {
    return this.track.children.length;
  };

  Carousel.prototype.pageWidth = function () {
    return this.track.clientWidth;
  };

  /* How many discrete swipes it takes to see everything. */
  Carousel.prototype.pageCount = function () {
    var pageWidth = this.pageWidth();
    if (pageWidth <= 0) return 0;
    /* 2px of slack absorbs sub-pixel rounding in the flex-basis calc(). */
    return Math.max(1, Math.ceil((this.track.scrollWidth - 2) / pageWidth));
  };

  Carousel.prototype.isOverflowing = function () {
    return this.track.scrollWidth - this.track.clientWidth > 2;
  };

  Carousel.prototype.refresh = function () {
    /* The container can be display:none or hidden when we first run (recently
       viewed starts hidden). Leave it alone; the ResizeObserver brings us back
       the moment it gets a width. */
    if (this.pageWidth() <= 0) return;

    var overflowing = this.isOverflowing();
    this.root.classList.toggle('sma-carousel--static', !overflowing);

    var pages = overflowing ? this.pageCount() : 0;
    if (pages !== this.pages) {
      this.pages = pages;
      this.buildDots();
    }

    this.updateState();
  };

  Carousel.prototype.buildDots = function () {
    if (!this.dotsContainer) return;

    this.dotsContainer.innerHTML = '';
    if (this.pages < 2) return;

    for (var i = 0; i < this.pages; i++) {
      var dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'sma-carousel__dot';
      dot.setAttribute('aria-label', 'Go to slide ' + (i + 1) + ' of ' + this.pages);
      dot.addEventListener('click', this.goToPage.bind(this, i));
      this.dotsContainer.appendChild(dot);
    }
  };

  Carousel.prototype.currentPageIndex = function () {
    var pageWidth = this.pageWidth();
    if (pageWidth <= 0) return 0;

    /* Clamp to the last page so a partially-filled final page still lights up
       its own dot rather than the one before it. */
    var maxScroll = this.track.scrollWidth - pageWidth;
    if (maxScroll > 0 && this.track.scrollLeft >= maxScroll - 2) {
      return Math.max(0, this.pages - 1);
    }
    return Math.min(Math.max(0, this.pages - 1), Math.round(this.track.scrollLeft / pageWidth));
  };

  Carousel.prototype.updateState = function () {
    var page = this.currentPageIndex();
    this.currentPage = page;

    if (this.dotsContainer) {
      var dots = this.dotsContainer.children;
      for (var i = 0; i < dots.length; i++) {
        var active = i === page;
        dots[i].classList.toggle('is-active', active);
        dots[i].setAttribute('aria-current', active ? 'true' : 'false');
      }
    }

    var atStart = this.track.scrollLeft <= 2;
    var atEnd = this.track.scrollLeft >= this.track.scrollWidth - this.track.clientWidth - 2;

    if (this.prevButton) this.prevButton.disabled = atStart;
    if (this.nextButton) this.nextButton.disabled = atEnd;
  };

  Carousel.prototype.onScroll = function () {
    if (this.ticking) return;
    this.ticking = true;
    window.requestAnimationFrame(
      function () {
        this.ticking = false;
        this.updateState();
      }.bind(this)
    );
  };

  Carousel.prototype.scrollTo = function (left) {
    var behavior = prefersReducedMotion() ? 'auto' : 'smooth';
    if (typeof this.track.scrollTo === 'function') {
      this.track.scrollTo({ left: left, behavior: behavior });
    } else {
      this.track.scrollLeft = left;
    }
  };

  Carousel.prototype.scrollByPage = function (direction) {
    this.scrollTo(this.track.scrollLeft + direction * this.pageWidth());
  };

  Carousel.prototype.goToPage = function (page) {
    this.scrollTo(page * this.pageWidth());
  };

  function init(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var nodes = scope.querySelectorAll(CAROUSEL);

    for (var i = 0; i < nodes.length; i++) {
      var node = nodes[i];

      /* Already wired: it may just have been measured while hidden (recently
         viewed starts hidden), so give it another chance to size itself. */
      if (node.dataset.smaCarouselInit === 'true') {
        if (node.smaCarousel && node.smaCarousel.track) node.smaCarousel.refresh();
        continue;
      }

      node.dataset.smaCarouselInit = 'true';
      node.smaCarousel = new Carousel(node);
    }
  }

  /* Recommendations and recently-viewed both swap their own innerHTML in after
     a fetch, so watch their wrappers and initialise whatever lands inside. */
  var pending = false;
  function observeHosts(root) {
    if (typeof MutationObserver !== 'function') return;

    var hosts = (root && root.querySelectorAll ? root : document).querySelectorAll(HOST);

    hosts.forEach(function (host) {
      if (host.dataset.smaCarouselObserved === 'true') return;
      host.dataset.smaCarouselObserved = 'true';

      new MutationObserver(function () {
        if (pending) return;
        pending = true;
        window.requestAnimationFrame(function () {
          pending = false;
          init(host);
        });
      }).observe(host, { childList: true, subtree: true });
    });
  }

  function start(root) {
    init(root);
    observeHosts(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      start();
    });
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', function (event) {
    start(event.target);
  });

  window.SmaCarousel = { init: init, observeHosts: observeHosts, start: start };
})();
